import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { IdlePerStepRow } from '../../types/dashboard';

interface Props {
  data: IdlePerStepRow[] | null;
}

function getBarColor(pct: number): string {
  if (pct < 20) return '#10b981';
  if (pct < 40) return '#f59e0b';
  return '#ef4444';
}

export default function IdlePerStepChart({ data }: Props) {
  if (!data || data.length === 0) {
    return <Placeholder text="No idle data available" />;
  }

  const chartData = data.map((r) => ({
    name: r.step_name || `Step ${r.step_number}`,
    idle_pct: r.avg_idle_pct,
  }));

  return (
    <div style={sectionStyle}>
      <h3 style={headingStyle}>Avg Idle % per Step</h3>
      <p style={descStyle}>Idle = gaps &gt; 3 min between consecutive user events</p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} layout="vertical" barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            tickFormatter={(v) => `${v}%`}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={130}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' }}
            formatter={(value) => `${Number(value).toFixed(1)}%`}
          />
          <Bar dataKey="idle_pct" radius={[0, 4, 4, 0]} name="Idle %">
            {chartData.map((entry, i) => (
              <Cell key={i} fill={getBarColor(entry.idle_pct)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160, color: '#9ca3af' }}>
      {text}
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 12,
  padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
};

const headingStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: '#111827',
  margin: '0 0 4px 0',
};

const descStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  margin: '0 0 16px 0',
};
